// src/pages/Points/Points.jsx
import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Stack,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
} from '@mui/material';
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
} from '@mui/x-data-grid';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  People as PeopleIcon,
  Stars as StarsIcon,
  SwapHoriz as SwapHorizIcon,
  AccountBalance as AccountBalanceIcon,
  EmojiEvents as TrophyIcon,
} from '@mui/icons-material';
import {
  useGetPointsStatsQuery,
  useGetPointsUsersQuery,
  useGetConversionsQuery,
} from '../../store/api/pointsMonitorApi';

// Stat Card Component
const StatCard = ({ title, value, subtitle, icon: Icon, color = 'primary', trend }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight="bold" color={`${color}.main`}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            backgroundColor: `${color}.light`,
            borderRadius: 2,
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon sx={{ color: `${color}.main` }} />
        </Box>
      </Stack>
      {trend !== undefined && (
        <Stack direction="row" alignItems="center" spacing={0.5} mt={1}>
          {trend >= 0 ? (
            <TrendingUpIcon fontSize="small" color="success" />
          ) : (
            <TrendingDownIcon fontSize="small" color="error" />
          )}
          <Typography variant="caption" color={trend >= 0 ? 'success.main' : 'error.main'}>
            {Math.abs(trend)}%
          </Typography>
        </Stack>
      )}
    </CardContent>
  </Card>
);

// Custom Toolbar
const CustomToolbar = () => (
  <GridToolbarContainer>
    <GridToolbarFilterButton />
    <GridToolbarExport />
  </GridToolbarContainer>
);

// Tab Panel
const TabPanel = ({ children, value, index, ...other }) => (
  <div role="tabpanel" hidden={value !== index} {...other}>
    {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
  </div>
);

const Points = () => {
  const [tab, setTab] = useState(0);
  const [usersPaginationModel, setUsersPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [conversionsPaginationModel, setConversionsPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [usersFilters, setUsersFilters] = useState({ search: '', minPoints: '', maxPoints: '' });
  const [conversionsFilters, setConversionsFilters] = useState({ search: '' });

  // API Queries
  const { data: statsData, isLoading: statsLoading, refetch: refetchStats } = useGetPointsStatsQuery();
  const { data: usersData, isLoading: usersLoading, refetch: refetchUsers } = useGetPointsUsersQuery({
    page: usersPaginationModel.page + 1,
    limit: usersPaginationModel.pageSize,
    ...usersFilters,
  });
  const { data: conversionsData, isLoading: conversionsLoading, refetch: refetchConversions } = useGetConversionsQuery({
    page: conversionsPaginationModel.page + 1,
    limit: conversionsPaginationModel.pageSize,
    ...conversionsFilters,
  });

  const stats = statsData?.data || {};
  const users = usersData?.data?.users || [];
  const usersPagination = usersData?.data?.pagination || { total: 0 };
  const conversions = conversionsData?.data?.conversions || [];
  const conversionsPagination = conversionsData?.data?.pagination || { total: 0 };

  const handleUsersFilterChange = useCallback((field, value) => {
    setUsersFilters((prev) => ({ ...prev, [field]: value }));
    setUsersPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const handleConversionsFilterChange = useCallback((field, value) => {
    setConversionsFilters((prev) => ({ ...prev, [field]: value }));
    setConversionsPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const handleRefresh = () => {
    refetchStats();
    refetchUsers();
    refetchConversions();
  };

  // Users DataGrid columns
  const usersColumns = [
    {
      field: 'phone',
      headerName: 'Phone',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" fontFamily="monospace">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'username',
      headerName: 'Username',
      width: 120,
    },
    {
      field: 'points',
      headerName: 'Points',
      width: 100,
      type: 'number',
      renderCell: (params) => (
        <Chip
          label={params.value?.toLocaleString() || 0}
          size="small"
          color={params.value > 100 ? 'primary' : 'default'}
        />
      ),
    },
    {
      field: 'balance',
      headerName: 'Balance',
      width: 110,
      type: 'number',
      valueFormatter: (value) => `K${(value || 0).toFixed(2)}`,
    },
    {
      field: 'lastActiveAt',
      headerName: 'Last Active',
      width: 120,
      type: 'dateTime',
      valueGetter: (value) => (value ? new Date(value) : null),
      valueFormatter: (value) => (value ? value.toLocaleDateString() : '-'),
    },
    {
      field: 'createdAt',
      headerName: 'Joined',
      width: 120,
      type: 'dateTime',
      valueGetter: (value) => (value ? new Date(value) : null),
      valueFormatter: (value) => (value ? value.toLocaleDateString() : '-'),
    },
  ];

  // Conversions DataGrid columns
  const conversionsColumns = [
    {
      field: 'phone',
      headerName: 'Phone',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" fontFamily="monospace">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'convertedPoints',
      headerName: 'Points',
      width: 100,
      type: 'number',
      renderCell: (params) => (
        <Chip label={params.value?.toLocaleString() || 0} size="small" color="warning" />
      ),
    },
    {
      field: 'cashCredited',
      headerName: 'Cash',
      width: 100,
      type: 'number',
      valueFormatter: (value) => `K${(value || 0).toFixed(2)}`,
    },
    {
      field: 'pointsBefore',
      headerName: 'Pts Before',
      width: 100,
      type: 'number',
    },
    {
      field: 'pointsAfter',
      headerName: 'Pts After',
      width: 100,
      type: 'number',
    },
    {
      field: 'balanceBefore',
      headerName: 'Bal Before',
      width: 100,
      type: 'number',
      valueFormatter: (value) => `K${(value || 0).toFixed(2)}`,
    },
    {
      field: 'balanceAfter',
      headerName: 'Bal After',
      width: 100,
      type: 'number',
      valueFormatter: (value) => `K${(value || 0).toFixed(2)}`,
    },
    {
      field: 'createdAt',
      headerName: 'Date',
      width: 160,
      type: 'dateTime',
      valueGetter: (value) => (value ? new Date(value) : null),
      valueFormatter: (value) => (value ? value.toLocaleString() : '-'),
    },
  ];

  const usersRows = users.map((user) => ({ id: user._id, ...user }));
  const conversionsRows = conversions.map((c) => ({ id: c._id, ...c }));

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Points Monitoring
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track points circulation, conversions, and user points activity
          </Typography>
        </Box>
        <Tooltip title="Refresh Data">
          <IconButton onClick={handleRefresh} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Stats Loading */}
      {statsLoading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Stats Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Users with Points"
            value={stats.users?.withAtLeastOnePoint?.toLocaleString() || 0}
            subtitle={`Out of ${stats.users?.totalUsers?.toLocaleString() || 0} total users`}
            icon={PeopleIcon}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Points in Circulation"
            value={stats.users?.totalPoints?.toLocaleString() || 0}
            subtitle={`Avg: ${stats.users?.avgPoints || 0} | Max: ${stats.users?.maxPoints?.toLocaleString() || 0}`}
            icon={StarsIcon}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Unconverted Points Value"
            value={`K${stats.users?.unconvertedCashValue || 0}`}
            subtitle="Total cash value if all points converted"
            icon={AccountBalanceIcon}
            color="error"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Cash Credited"
            value={`K${stats.conversions?.allTime?.cash || 0}`}
            subtitle={`${stats.conversions?.allTime?.count?.toLocaleString() || 0} conversions total`}
            icon={SwapHorizIcon}
            color="success"
          />
        </Grid>
      </Grid>

      {/* Secondary Stats */}
      <Grid container spacing={3} mb={3}>
        {/* Points Distribution */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Points Distribution
            </Typography>
            <Stack spacing={1.5}>
              {stats.distribution &&
                Object.entries(stats.distribution).map(([range, count]) => {
                  const total = stats.users?.withAtLeastOnePoint || 1;
                  const percent = ((count / total) * 100).toFixed(1);
                  return (
                    <Box key={range}>
                      <Stack direction="row" justifyContent="space-between" mb={0.5}>
                        <Typography variant="body2">{range} pts</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {count} ({percent}%)
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={parseFloat(percent)}
                        sx={{ height: 6, borderRadius: 1 }}
                      />
                    </Box>
                  );
                })}
            </Stack>
          </Paper>
        </Grid>

        {/* Conversion Stats by Period */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Conversion Summary
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Period</TableCell>
                    <TableCell align="right">Count</TableCell>
                    <TableCell align="right">Points</TableCell>
                    <TableCell align="right">Cash</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Today</TableCell>
                    <TableCell align="right">{stats.conversions?.today?.count || 0}</TableCell>
                    <TableCell align="right">{stats.conversions?.today?.points?.toLocaleString() || 0}</TableCell>
                    <TableCell align="right">K{stats.conversions?.today?.cash || 0}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>This Week</TableCell>
                    <TableCell align="right">{stats.conversions?.week?.count || 0}</TableCell>
                    <TableCell align="right">{stats.conversions?.week?.points?.toLocaleString() || 0}</TableCell>
                    <TableCell align="right">K{stats.conversions?.week?.cash || 0}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>This Month</TableCell>
                    <TableCell align="right">{stats.conversions?.month?.count || 0}</TableCell>
                    <TableCell align="right">{stats.conversions?.month?.points?.toLocaleString() || 0}</TableCell>
                    <TableCell align="right">K{stats.conversions?.month?.cash || 0}</TableCell>
                  </TableRow>
                  <TableRow sx={{ '& td': { fontWeight: 'bold' } }}>
                    <TableCell>All Time</TableCell>
                    <TableCell align="right">{stats.conversions?.allTime?.count?.toLocaleString() || 0}</TableCell>
                    <TableCell align="right">{stats.conversions?.allTime?.points?.toLocaleString() || 0}</TableCell>
                    <TableCell align="right">K{stats.conversions?.allTime?.cash || 0}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <Box mt={2}>
              <Typography variant="body2" color="text.secondary">
                Unique converters: <strong>{stats.conversions?.uniqueConverters || 0}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Avg conversion: <strong>{stats.conversions?.allTime?.avgConversion || 0} pts</strong>
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Top Point Holders */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <TrophyIcon color="warning" />
              <Typography variant="h6">Top Point Holders</Typography>
            </Stack>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell align="right">Points</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.topHolders?.slice(0, 10).map((user, idx) => (
                    <TableRow key={user._id}>
                      <TableCell>
                        {idx < 3 ? (
                          <Chip
                            label={idx + 1}
                            size="small"
                            color={idx === 0 ? 'warning' : idx === 1 ? 'default' : 'error'}
                            sx={{ minWidth: 24 }}
                          />
                        ) : (
                          idx + 1
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {user.phone?.slice(-8)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="bold">{user.points?.toLocaleString()}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Tabs for Users & Conversions Tables */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab label="Users with Points" />
          <Tab label="Conversion History" />
        </Tabs>
      </Paper>

      {/* Users Tab */}
      <TabPanel value={tab} index={0}>
        {/* Users Filters */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <TextField
              size="small"
              placeholder="Search phone..."
              value={usersFilters.search}
              onChange={(e) => handleUsersFilterChange('search', e.target.value)}
              sx={{ minWidth: 200 }}
            />
            <TextField
              size="small"
              type="number"
              placeholder="Min points"
              value={usersFilters.minPoints}
              onChange={(e) => handleUsersFilterChange('minPoints', e.target.value)}
              sx={{ width: 120 }}
            />
            <TextField
              size="small"
              type="number"
              placeholder="Max points"
              value={usersFilters.maxPoints}
              onChange={(e) => handleUsersFilterChange('maxPoints', e.target.value)}
              sx={{ width: 120 }}
            />
          </Stack>
        </Paper>

        {/* Users Table */}
        <Paper sx={{ height: 500 }}>
          <DataGrid
            rows={usersRows}
            columns={usersColumns}
            loading={usersLoading}
            rowCount={usersPagination.total}
            pageSizeOptions={[10, 25, 50, 100]}
            paginationModel={usersPaginationModel}
            paginationMode="server"
            onPaginationModelChange={setUsersPaginationModel}
            slots={{ toolbar: CustomToolbar }}
          />
        </Paper>
      </TabPanel>

      {/* Conversions Tab */}
      <TabPanel value={tab} index={1}>
        {/* Conversions Filters */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <TextField
              size="small"
              placeholder="Search phone..."
              value={conversionsFilters.search}
              onChange={(e) => handleConversionsFilterChange('search', e.target.value)}
              sx={{ minWidth: 200 }}
            />
          </Stack>
        </Paper>

        {/* Conversions Table */}
        <Paper sx={{ height: 500 }}>
          <DataGrid
            rows={conversionsRows}
            columns={conversionsColumns}
            loading={conversionsLoading}
            rowCount={conversionsPagination.total}
            pageSizeOptions={[10, 25, 50, 100]}
            paginationModel={conversionsPaginationModel}
            paginationMode="server"
            onPaginationModelChange={setConversionsPaginationModel}
            slots={{ toolbar: CustomToolbar }}
          />
        </Paper>
      </TabPanel>
    </Box>
  );
};

export default Points;
